/**
 * Learning Session Details Page
 * 
 * A comprehensive learning session details page that provides:
 * - Session information and statistics
 * - Chat history display
 * - Continue session functionality
 * - Professional UI with session insights
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import ApiService from '../../services/apiService';
import { formatDate } from '../../utils/dateUtils';

const LearningSessionDetails = () => {
  const { profileId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [teacher, setTeacher] = useState(null);
  const [messages, setMessages] = useState([]);
  const [profile, setProfile] = useState(null);

  // Fetch session details
  const fetchSessionDetails = useCallback(async () => {
    try {
      setLoading(true);
      
      if (!sessionId) {
        setError('No session ID provided');
        setLoading(false);
        return;
      }

      const userId = profileId || currentUser?.uid;
      if (!userId) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      // Fetch session data
      const sessionData = await ApiService.getLearningSession(sessionId);
      console.log('Session data:', sessionData);
      setSession(sessionData.learningSession);

      // Fetch teacher data if available
      if (sessionData.teacherId) {
        const teacherData = await ApiService.getTeacher(sessionData.teacherId);
        console.log('Teacher data:', teacherData);
        setTeacher(teacherData);
      }

      // Fetch chat history using learningSessionId
      if (sessionData.learningSession.id) {
        const token = await currentUser.getIdToken();
        const historyData = await ApiService.getLearningSessionMessages(sessionData.learningSession.id, token);
        console.log('Chat history:', historyData);
        setMessages(historyData || []);
      }

      // Fetch profile data
      const profileData = await ApiService.getProfile(userId);
      setProfile(profileData);

    } catch (err) {
      console.error('Error fetching session details:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [sessionId, profileId, currentUser]);

  // Initialize data on mount
  useEffect(() => {
    fetchSessionDetails();
  }, [fetchSessionDetails]);

  // Handle continue session
  const handleContinueSession = useCallback(() => {
    if (!session || !teacher) {
      setError('Session or teacher data not available');
      return;
    }

    navigate(`/chat/${profileId || currentUser?.uid}/realtime`, {
      state: {
        teacher: teacher,
        sessionId: sessionId,
        isResuming: true
      }
    });
  }, [session, teacher, sessionId, profileId, currentUser, navigate]);

  // Handle delete session
  const handleDeleteSession = useCallback(async () => {
    if (!window.confirm('Are you sure you want to delete this learning session? This action cannot be undone.')) {
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      await ApiService.deleteLearningSession(sessionId, token);
      navigate(`/dashboard`);
    } catch (err) {
      console.error('Error deleting session:', err);
      setError(err.message);
    }
  }, [sessionId, currentUser, navigate]);

  // Format time display
  const formatTime = (minutes) => {
    if (!minutes) return '0 min';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins} min`;
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 mb-4 animate-pulse">
              <svg className="animate-spin h-10 w-10 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Loading Session Details</h3>
            <p className="text-gray-600">Fetching your learning session information...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Session</h3>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Session Not Found</h3>
            <p className="text-gray-600 mb-6">The learning session you're looking for doesn't exist or has been deleted.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 pt-28 pb-6">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="font-medium">Back to Dashboard</span>
            </button>
          </div>
          
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Learning Session Details</h1>
                <p className="text-gray-600">
                  Session with {teacher?.name || 'Unknown Teacher'} • {formatDate(session.createdAt, 'datetime')}
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={handleContinueSession}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all duration-300 hover:shadow-lg transform hover:scale-105"
                >
                  Continue Session
                </button>
                <button
                  onClick={handleDeleteSession}
                  className="bg-red-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-red-700 transition-all duration-300"
                >
                  Delete Session
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Session Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Duration</h3>
                <p className="text-2xl font-bold text-blue-600">{formatTime(session.time_minutes)}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Attention</h3>
                <p className="text-2xl font-bold text-green-600">{session.attention_percentage || 0}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Messages</h3>
                <p className="text-2xl font-bold text-purple-600">{messages.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Cost</h3>
                <p className="text-2xl font-bold text-yellow-600">${(session.total_cost_usd || 0).toFixed(4)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Teacher Information */}
        {teacher && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Teacher Information</h2>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full overflow-hidden">
                {teacher.picture ? (
                  <img
                    src={teacher.picture}
                    alt={teacher.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">
                      {teacher.name.charAt(0)}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900">{teacher.name}</h3>
                <p className="text-gray-600">{teacher.specialty} • {teacher.level}</p>
                {teacher.biography && (
                  <p className="text-sm text-gray-500 mt-2">{teacher.biography}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Lesson Plan */}
        {session.lesson_plan && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Lesson Plan</h2>
            <div className="prose max-w-none">
              <div className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">
                {session.lesson_plan}
              </div>
            </div>
          </div>
        )}

        {/* Session Summary */}
        {session.summary && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Session Summary</h2>
            <div className="prose max-w-none">
              <div className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed">
                {session.summary}
              </div>
            </div>
          </div>
        )}

        {/* Chat History */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Chat History</h2>
            <div className="text-sm text-gray-500">
              {messages.length} message{messages.length !== 1 ? 's' : ''}
            </div>
          </div>

          {messages.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Messages Yet</h3>
              <p className="text-gray-600">Start the session to begin your conversation with the teacher.</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {messages.length > 0 && messages.map((message, index) => {
                const isStudentMessage = message.role === 'student';
                const cleanMessage = message.content || '';
                const timestamp = message.createdAt;
                
                return (
                  <div key={index} className={`flex ${isStudentMessage ? 'justify-end' : 'justify-start'}`}>
                    <div className={`flex gap-3 max-w-[80%] ${isStudentMessage ? 'flex-row-reverse' : 'flex-row'}`}>
                      {/* Avatar */}
                      <div className="flex-shrink-0">
                        {isStudentMessage ? (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-md">
                            <span className="text-white font-bold text-sm">
                              {profile?.firstName?.charAt(0) || 'S'}
                            </span>
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-500 to-blue-600 flex items-center justify-center shadow-md">
                            <span className="text-white font-bold text-sm">
                              {teacher?.name?.charAt(0) || 'T'}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Message Bubble */}
                      <div>
                        <div className={`text-xs font-medium mb-1 ${isStudentMessage ? 'text-right' : 'text-left'}`}>
                          <span className={isStudentMessage ? 'text-blue-600' : 'text-green-600'}>
                            {isStudentMessage ? (profile?.firstName || 'You') : (teacher?.name || 'Teacher')}
                          </span>
                          {timestamp && (
                            <span className="text-gray-500 ml-2">
                              {formatDate(timestamp, 'time')}
                            </span>
                          )}
                        </div>
                        <div 
                          className={`p-3 rounded-2xl shadow-sm ${
                            isStudentMessage 
                              ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-tr-none' 
                              : 'bg-white text-gray-900 border border-gray-200 rounded-tl-none'
                          }`}
                        >
                          <p className="text-sm leading-relaxed">{cleanMessage}</p>
                          {/* Show cost if available */}
                          {message.cost_usd && message.cost_usd > 0 && (
                            <div className="text-xs opacity-75 mt-1">
                              Cost: ${message.cost_usd.toFixed(6)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LearningSessionDetails;
